import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import type MetricsService from 'garaje/services/metrics';
import type PlanFeaturesService from 'garaje/services/plan-features';
import type StateService from 'garaje/services/state';
import type TrialService from 'garaje/services/trial';
import type CtaEventProperties from 'garaje/types/cta-event-properties';
import { alias, equal } from 'macro-decorators';

interface FeatureOrUpgradeFieldSignature {
  Args: {
    fieldClass: string;
    fieldFor: string;
    fieldText: string;
    featureName: string;
    isAuthorized: boolean;
    isSlack: boolean;
    level: string;
    logCTAEvents: boolean;
    onUpgrade?: () => void | null;
    shouldCheckMinLevel: boolean;
    upgradeButtonText?: string;
    upgradeClass?: string;
    upgradeQueryParams: { plan: 'standard' | 'premium'; period: 'monthly' | 'yearly' };
    vrOnly: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * @param {String}    fieldClass
 * @param {String}    fieldFor
 * @param {String}    fieldText
 */
export default class FeatureOrUpgradeField extends Component<FeatureOrUpgradeFieldSignature> {
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;
  @service declare planFeatures: PlanFeaturesService;
  @service declare state: StateService;
  @service declare trial: TrialService;

  @alias('state.vrSubscription') declare vrSubscription: StateService['vrSubscription'];
  @alias('vrSubscription.hasTrialDaysLeft') declare hasTrialDaysLeft: boolean;
  @alias('trial.trials') declare plans: TrialService['trials'];
  @equal('vrSubscription.plan', 'basic') declare isBasicUser: boolean;

  get levelString(): string {
    const advertisedLevel = this.planFeatures.calcAdvertisedPlanId(this.args.level);
    return this.planFeatures.planIdToName(advertisedLevel);
  }

  get levelCapitalize(): string {
    return capitalize(this.levelString || '');
  }

  get showSignUpForVR(): boolean {
    const vrActive = isPresent(this.vrSubscription);

    return !vrActive && this.args.vrOnly && !this.args.isSlack;
  }

  get canAccessFeature(): boolean {
    const trial = this.trial;
    // eslint-disable-next-line ember/no-get
    const canTrialFeature = get(trial, 'canTrialFeature');
    const isAuthorized = this.args.isAuthorized;
    const level = this.args.level;
    const shouldCheckMinLevel = this.args.shouldCheckMinLevel;
    const hasDeliveries = !!this.state?.features?.canAccessDeliveriesApplication;

    if (isAuthorized) {
      return true;
    }
    if (this.args.isSlack && hasDeliveries) {
      return true;
    }
    if (canTrialFeature) {
      return trial.canAccess(level);
    }
    if (shouldCheckMinLevel) {
      return this._hasMinLevel(level);
    }
    return false;
  }

  get upgradeButtonText(): string {
    if (this.args.upgradeButtonText) return this.args.upgradeButtonText;
    if (this.levelCapitalize) return `Upgrade to ${this.levelCapitalize}`;
    return `Upgrade`;
  }

  _hasMinLevel(level: string): boolean {
    // eslint-disable-next-line ember/no-get
    const actualPlan = this.plans.indexOf(get(this, 'vrSubscription.plan') as string);
    const requiredPlan = this.plans.indexOf(level);
    return actualPlan >= requiredPlan;
  }

  @action
  upgrade(): void {
    if (typeof this.args.onUpgrade === 'function') {
      this.args.onUpgrade();
    }
  }

  ctaEventProperties(intent: string, text: string): CtaEventProperties {
    return {
      cta_id: this.args.featureName ? `${this.args.featureName}_feature_or_upgrade_field` : 'feature_or_upgrade_field',
      cta_title: null,
      cta_body: null,
      cta_type: 'field',
      cta_clickable_type: 'link',
      cta_clickable_text: text,
      cta_intent: intent,
    };
  }

  get shouldLogCTAEvents(): boolean {
    // default behavior is to log, unless @logCTAEvents is explicitly false (not just falsy)
    return this.args.logCTAEvents !== false;
  }

  @action
  logCTAViewedEvent(intent: string, element: HTMLElement): void {
    if (this.shouldLogCTAEvents) {
      this.metrics.trackEvent('CTA Viewed', this.ctaEventProperties(intent, element.innerText.trim()));
    }
  }

  @action
  logCTAClickedEvent(intent: string, event: Event): void {
    if (this.shouldLogCTAEvents) {
      this.metrics.trackEvent(
        'CTA Clicked',
        this.ctaEventProperties(
          intent,
          (event.target as null | (EventTarget & { innerText: string }))!.innerText.trim(),
        ),
      );
    }
  }

  <template>
    <div data-test-feature-or-upgrade-field ...attributes>
      <label class={{@fieldClass}} for={{@fieldFor}}>{{@fieldText}}</label>
      {{#if this.canAccessFeature}}
        {{yield}}
      {{else if this.trial.canTrialFeature}}
        <LinkTo
          @route="billing"
          @query={{hash showPlan=this.levelString}}
          class="input disabled employee-assistant-cta"
          {{didInsert (fn this.logCTAViewedEvent "trial")}}
          {{on "click" (fn this.logCTAClickedEvent "trial")}}
          data-test-trial-upgrade-button
        >
          Trial
          {{this.levelCapitalize}}
        </LinkTo>
      {{else if @upgradeQueryParams}}
        <LinkTo
          data-test-qs-upgrade-button
          @route="billing.subscribe"
          @query={{@upgradeQueryParams}}
          {{didInsert (fn this.logCTAViewedEvent "upgrade")}}
          {{on "click" (fn this.logCTAClickedEvent "upgrade")}}
          class="input disabled employee-assistant-cta"
        >
          Upgrade to
          {{this.levelCapitalize}}
        </LinkTo>
      {{else}}
        <LinkTo
          @route="billing.plans"
          {{didInsert (fn this.logCTAViewedEvent "upgrade")}}
          {{on "click" (fn this.logCTAClickedEvent "upgrade")}}
          data-test-upgrade-button
          class="input disabled employee-assistant-cta"
        >
          Upgrade to
          {{this.levelCapitalize}}
        </LinkTo>
      {{/if}}
    </div>
  </template>
}
